import { CopyIconButton } from "@aurem/ui";
import { UUID } from "components/display/UUID";
import { FileText } from "lucide-react";
import { Link } from "react-router-dom";
import {
  gql,
  InternalAuditTrailOrderByColumn,
  InternalAuditTrailsFilterField,
} from "types";
import { FieldType, InternalResourceConfig } from "../lib/internal-resources";

const RESOURCE_URL_MAP: Record<string, (id: string) => string> = {
  CompanyEntity: (id) => `/companies/${id}/dashboard/overview`,
};

const renderResourceId = (value: unknown, item: unknown) => {
  const id = String(value ?? "");
  if (!id || id === "null" || id === "undefined") return "-";

  const auditItem = item as { resourceType?: string };
  const resourceType = auditItem?.resourceType;
  const urlGenerator = resourceType ? RESOURCE_URL_MAP[resourceType] : null;

  if (urlGenerator) {
    return (
      <div className="inline-flex items-center gap-1 group">
        <Link
          to={urlGenerator(id)}
          className="text-primary-600 hover:underline"
          onClick={(e) => e.stopPropagation()}
        >
          <UUID value={id} showCopy={false} />
        </Link>
        <CopyIconButton
          copyContent={id}
          size="xs"
          variant="ghost"
          className="opacity-0 group-hover:opacity-100"
        />
      </div>
    );
  }

  return <UUID value={id} />;
};

const GET_AUDIT_TRAIL_LIST = gql(/* GraphQL */ `
  query InternalAuditTrailList(
    $pagination: Pagination
    $filters: InternalAuditTrailsFiltersExpression
    $orderBy: InternalAuditTrailOrderByInput
  ) {
    internalAuditTrailList(
      pagination: $pagination
      filters: $filters
      orderBy: $orderBy
    ) {
      count
      list {
        id
        resourceType
        resourceId
        event
        userId
        user {
          id
          profile {
            name
          }
        }
        object
        diff
        createdAt
      }
    }
  }
`);

export const auditTrailResourceConfig: InternalResourceConfig = {
  resourceName: "audit-trail",
  displayNameKey: "apps.admin.internalResources.auditTrail.title",
  basePath: "/internal/data/audit-trail",

  listQuery: GET_AUDIT_TRAIL_LIST,
  listQueryName: "internalAuditTrailList",

  fields: [
    {
      key: "event",
      labelKey: "apps.admin.internalResources.auditTrail.columns.event",
      type: FieldType.Enum,
      enumValues: {
        INSERT: { label: "Created", color: "success" },
        UPDATE: { label: "Updated", color: "primary" },
        REMOVE: { label: "Deleted", color: "danger" },
      },
      sortKey: InternalAuditTrailOrderByColumn.Event,
      width: 120,
    },
    {
      key: "resourceType",
      labelKey: "apps.admin.internalResources.auditTrail.columns.resourceType",
      type: FieldType.String,
      sortKey: InternalAuditTrailOrderByColumn.ResourceType,
      width: 150,
    },
    {
      key: "resourceId",
      labelKey: "apps.admin.internalResources.auditTrail.columns.resourceId",
      type: FieldType.Uuid,
      format: renderResourceId,
      width: 200,
    },
    {
      key: "object",
      labelKey: "apps.admin.internalResources.auditTrail.columns.object",
      type: FieldType.JSON,
      width: 120,
    },
    {
      key: "diff",
      labelKey: "apps.admin.internalResources.auditTrail.columns.diff",
      type: FieldType.JSON,
      width: { min: 100, grow: 1 },
    },
    {
      key: "userId",
      labelKey: "apps.admin.internalResources.auditTrail.columns.userId",
      type: FieldType.User,
      width: 200,
    },
    {
      key: "createdAt",
      labelKey: "apps.admin.internalResources.auditTrail.columns.createdAt",
      type: FieldType.DateTime,
      sortKey: InternalAuditTrailOrderByColumn.CreatedAt,
      width: 160,
    },
  ],

  list: {
    defaultSort: {
      column: InternalAuditTrailOrderByColumn.CreatedAt,
      direction: "desc",
    },
    searchFields: [
      InternalAuditTrailsFilterField.ResourceType,
      InternalAuditTrailsFilterField.ResourceId,
      InternalAuditTrailsFilterField.Event,
    ],
  },

  navigation: {
    icon: <FileText size={16} />,
    order: 1,
  },
};
